import {THREE} from '../base/three-defs.js';
import * as entity from '../base/entity.js';
import * as passes from '../base/passes.js';
import {player_entity} from './player-entity.js';

const AXIS_X = new THREE.Vector3(1, 0, 0);
const PITCH_Q_TMP = new THREE.Quaternion();
const VIEW_Q_TMP = new THREE.Quaternion();
const EYE_TMP = new THREE.Vector3();
const OFFSET_TMP = new THREE.Vector3();
const LOOKAT_TMP = new THREE.Vector3();

// First person: eye sits this far above the player origin (which is snapped
// to terrain height + 1.5 by the character controller), ~4.1 units above
// the terrain, comfortably clear of grass blade tips (~2.2 max).
const EYE_HEIGHT = 2.6;

// Exponential smoothing time constant for look rotation; raw mouse deltas
// arrive quantized per frame, and this glides the view through them.
const LOOK_SMOOTHING_TAU = 0.06;

export const player_camera = (() => {

  class PlayerCamera extends entity.Component {
    static CLASS_NAME = 'PlayerCamera';

    get NAME() {
      return PlayerCamera.CLASS_NAME;
    }

    constructor(params) {
      super();

      this.params_ = params;
      this.camera_ = params.camera;
      this.pitch_ = 0;
      this.pitchTarget_ = 0;
      this.lookSensitivity_ = 0.001;
      this.firstPerson_ = true;
      this.snapThirdPerson_ = false;
      this.currentPosition_ = new THREE.Vector3();
      this.currentLookat_ = new THREE.Vector3();
    }

    InitEntity() {
      this.SetPass(passes.Passes.CAMERA);

      window.addEventListener('keydown', (e) => {
        if (e.keyCode == 84) {  // T
          this.firstPerson_ = !this.firstPerson_;
          this.snapThirdPerson_ = true;
        }
      }, false);
    }

    Update(timeElapsed) {
      const input = this.Parent.GetComponent('PlayerInput');
      if (input) {
        // Mouse is direct pixel deltas; the look stick / gamepad pitch at
        // a rate. Clamp matches surreal-meadow's (-1.2 down, 1.0 up).
        this.pitchTarget_ -=
            input.current_.mouseDeltaY * player_entity.MOUSE_SENSITIVITY;
        this.pitchTarget_ +=
            input.current_.lookY * player_entity.PITCH_LOOK_SPEED * timeElapsed;
        this.pitchTarget_ = Math.max(-1.2, Math.min(1.0, this.pitchTarget_));
      }
      const smoothT = 1.0 - Math.exp(-timeElapsed / LOOK_SMOOTHING_TAU);
      this.pitch_ += (this.pitchTarget_ - this.pitch_) * smoothT;

      PITCH_Q_TMP.setFromAxisAngle(AXIS_X, -this.pitch_);
      const viewQ = VIEW_Q_TMP.copy(this.Parent.Quaternion).multiply(PITCH_Q_TMP);

      if (this.firstPerson_) {
        EYE_TMP.copy(this.Parent.Position);
        EYE_TMP.y += EYE_HEIGHT;

        LOOKAT_TMP.set(0, 0, 1);
        LOOKAT_TMP.applyQuaternion(viewQ);
        LOOKAT_TMP.add(EYE_TMP);

        this.camera_.position.copy(EYE_TMP);
        this.camera_.lookAt(LOOKAT_TMP);
        return;
      }

      // Third person: smoothed chase camera behind the player.
      const idealOffset = OFFSET_TMP.set(0, 3, -8);
      idealOffset.applyQuaternion(viewQ);
      idealOffset.add(this.Parent.Position);

      const idealLookat = LOOKAT_TMP.set(0, 1.25, 4);
      idealLookat.applyQuaternion(viewQ);
      idealLookat.add(this.Parent.Position);

      // Keep the camera from clipping into the terrain.
      const terrain = this.FindEntity('terrain');
      if (terrain) {
        const terrainComponent = terrain.GetComponent('TerrainComponent');
        if (terrainComponent.IsReady()) {
          const height = terrainComponent.GetHeight(idealOffset.x, idealOffset.z);
          idealOffset.y = Math.max(idealOffset.y, height + 0.75);
        }
      }

      if (this.snapThirdPerson_) {
        this.snapThirdPerson_ = false;
        this.currentPosition_.copy(idealOffset);
        this.currentLookat_.copy(idealLookat);
      } else {
        const t = 1.0 - Math.pow(0.0001, timeElapsed);
        this.currentPosition_.lerp(idealOffset, t);
        this.currentLookat_.lerp(idealLookat, t);
      }

      this.camera_.position.copy(this.currentPosition_);
      this.camera_.lookAt(this.currentLookat_);
    }
  }

  return {
    PlayerCamera: PlayerCamera
  };

})();
